import { convertHtmlStringValueToPlainTextLines } from '@AppModels/UiUtils';

/**
 * Rewrites both clipboard flavours on copy from the read-only viewer, so this page's computed background,
 * colour and font stop painting over the paste target's template. Merge-tag chips become `{{token}}` text.
 */

/** Past these bounds we leave the clipboard alone rather than walk the fragment. */
const InputCharsMax = 4 * 1024 * 1024;
const NodesMax = 50000;
/** Serializing the result recurses, so cap nesting well above anything real markup reaches. */
const DepthMax = 200;

const AllowedTags = new Set([
	'A',
	'B',
	'BLOCKQUOTE',
	'BR',
	'DIV',
	'EM',
	'H1',
	'H2',
	'H3',
	'H4',
	'H5',
	'H6',
	'HR',
	'I',
	'IMG',
	'LI',
	'OL',
	'P',
	'S',
	'SPAN',
	'STRIKE',
	'STRONG',
	'SUB',
	'SUP',
	'TABLE',
	'TBODY',
	'TD',
	'TFOOT',
	'TH',
	'THEAD',
	'TR',
	'U',
	'UL',
]);

/** Dropped with their subtrees — these carry no authored text. */
const DiscardedTags = new Set([
	'APPLET',
	'AUDIO',
	'BUTTON',
	'CANVAS',
	'EMBED',
	'FORM',
	'HEAD',
	'IFRAME',
	'INPUT',
	'LINK',
	'META',
	'NOSCRIPT',
	'OBJECT',
	'SCRIPT',
	'SELECT',
	'STYLE',
	'TEXTAREA',
	'TITLE',
	'VIDEO',
]);

/** SVG and MathML report lowercase tag names that would slip past the sets above, so match them by namespace. */
const HtmlNamespaceUri = 'http://www.w3.org/1999/xhtml';

/** Whitespace between these never renders, so it must not reach the text flavour as blank lines. */
const BlockTags = new Set([
	'BLOCKQUOTE',
	'DIV',
	'H1',
	'H2',
	'H3',
	'H4',
	'H5',
	'H6',
	'HR',
	'LI',
	'OL',
	'P',
	'TABLE',
	'TBODY',
	'TD',
	'TFOOT',
	'TH',
	'THEAD',
	'TR',
	'UL',
]);

/** HTML collapses only ASCII whitespace; `&nbsp;` is content, so it is left alone. */
const WhitespaceRun = /[ \t\n\r\f]+/g;

const AllowedAttributes: Record<string, Set<string>> = {
	A: new Set(['href', 'target', 'title']),
	IMG: new Set(['alt', 'height', 'src', 'width']),
	OL: new Set(['start', 'type']),
	TD: new Set(['colspan', 'rowspan']),
	TH: new Set(['colspan', 'rowspan']),
};

const LinkSchemes = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const ImageSchemes = new Set(['http:', 'https:']);

const BoldWeights = new Set(['600', '700', '800', '900', 'bold', 'bolder']);
const ItalicStyles = new Set(['italic', 'oblique']);
/** `left` is the browser default Chrome stamps on everything, so only explicit deviations survive. */
const MeaningfulAlignments = new Set(['center', 'justify', 'right']);
const VerticalAlignments = new Set(['sub', 'super']);

interface ISanitizeCopiedHtmlOptions {
	baseUrl?: string;
	/** Placeholder `data-placeholder-value` -> `{{token}}`. */
	placeholderSymbols?: Record<string, string>;
	/** The colour this surface renders text in. Text wearing it is environmental, so it is dropped. */
	surfaceColor?: string;
	/** The colour this surface paints behind the content. Dropped for the same reason. */
	surfaceBackgroundColor?: string;
}

interface ISanitizedClipboardContent {
	html: string;
	text: string;
}

/** Word writes `background:white` and `color:black` by name, so they must compare equal to their hex. */
const NamedColors: Record<string, string> = { black: '#000000', white: '#ffffff' };

const toHexColor = (value?: string | null) => {
	if (!value) {
		return null;
	}
	const text = value.trim().toLowerCase();
	if (text === 'transparent') {
		return 'transparent';
	}
	const named = NamedColors[text];
	if (named) {
		return named;
	}
	const rgb = text.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*(?:[,/]\s*([\d.]+)\s*)?\)$/);
	if (rgb) {
		const alpha = rgb[4] === undefined ? 1 : parseFloat(rgb[4]);
		if (alpha === 0) {
			return 'transparent';
		}
		// An opaque hex would turn a faint tint into a solid block, so a translucent colour stays as written.
		if (alpha < 1) {
			return text;
		}
		return `#${[rgb[1], rgb[2], rgb[3]]
			.map(part => {
				const byte = Math.max(0, Math.min(255, Math.round(parseFloat(part))));
				return `${byte < 16 ? '0' : ''}${byte.toString(16)}`;
			})
			.join('')}`;
	}
	const short = text.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
	if (short) {
		return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
	}
	const long = text.match(/^#([0-9a-f]{6})$/);
	return long ? `#${long[1]}` : text;
};

/**
 * White reads as environmental wherever it comes from: every surface we copy from paints white, and
 * a white highlight on a white page is invisible, so nobody chose it on purpose.
 */
const isEnvironmentalBackground = (color: string | null, surface: string | null) =>
	!color || color === 'transparent' || color === '#ffffff' || color === surface;

/** Brightest channel and channel spread at or below which a colour reads as body-text black. */
const NeutralDarkChannelMax = 0x33;
const NeutralDarkSpreadMax = 0x10;

/**
 * Body text is near-black but rarely the surface's exact black: Google Docs imports stamp `#0e101a` on
 * every run. Treating every neutral near-black as ambient lets the paste target's body colour apply.
 */
const isEnvironmentalColor = (color: string | null, surface: string | null) => {
	if (!color || color === surface) {
		return true;
	}
	const hex = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/);
	if (!hex) {
		return false;
	}
	const channels = [hex[1], hex[2], hex[3]].map(part => parseInt(part, 16));
	const brightest = Math.max(...channels);
	return brightest <= NeutralDarkChannelMax && brightest - Math.min(...channels) <= NeutralDarkSpreadMax;
};

const keptDecorations = (element: HTMLElement) => {
	const declared = element.style.textDecorationLine || element.style.textDecoration || '';
	const kept: string[] = [];
	if (declared.includes('underline')) {
		kept.push('underline');
	}
	if (declared.includes('line-through')) {
		kept.push('line-through');
	}
	return kept;
};

const rewriteStyle = (element: HTMLElement, surfaceColor: string | null, surfaceBackground: string | null) => {
	const { style } = element;
	const weight = (style.fontWeight || '').toLowerCase();
	const decorations = keptDecorations(element);
	const color = toHexColor(style.color);
	const background = toHexColor(style.backgroundColor);
	const listStyleType = style.listStyleType;

	const kept: string[] = [];
	if (BoldWeights.has(weight)) {
		kept.push('font-weight:bold');
	}
	if (ItalicStyles.has((style.fontStyle || '').toLowerCase())) {
		kept.push('font-style:italic');
	}
	if (decorations.length) {
		kept.push(`text-decoration:${decorations.join(' ')}`);
	}
	if (MeaningfulAlignments.has((style.textAlign || '').toLowerCase())) {
		kept.push(`text-align:${style.textAlign.toLowerCase()}`);
	}
	if (VerticalAlignments.has((style.verticalAlign || '').toLowerCase())) {
		kept.push(`vertical-align:${style.verticalAlign.toLowerCase()}`);
	}
	if (listStyleType && ['UL', 'OL', 'LI'].includes(element.tagName)) {
		kept.push(`list-style-type:${listStyleType}`);
	}
	if (!isEnvironmentalColor(color, surfaceColor)) {
		kept.push(`color:${color}`);
	}
	if (!isEnvironmentalBackground(background, surfaceBackground)) {
		kept.push(`background-color:${background}`);
	}

	element.removeAttribute('style');
	if (kept.length) {
		element.setAttribute('style', kept.join(';'));
	}
	return weight;
};

const isAbsoluteUrl = (url: string) => {
	try {
		new URL(url);
		return true;
	} catch {
		return false;
	}
};

/**
 * Returns the URL to write back, or null when its scheme is not allowed. Relative URLs become absolute
 * because they would break once pasted outside this page; absolute ones are left byte-for-byte so
 * merge tokens inside them are not percent-encoded.
 */
const resolveAllowedUrl = (url: string, schemes: Set<string>, baseUrl?: string) => {
	try {
		const resolved = new URL(url, baseUrl);
		if (!schemes.has(resolved.protocol)) {
			return null;
		}
		return isAbsoluteUrl(url) ? url : resolved.href;
	} catch {
		return null;
	}
};

/** Returns false when the element should be dropped outright. */
const rewriteAttributes = (element: HTMLElement, baseUrl?: string) => {
	const allowed = AllowedAttributes[element.tagName];
	for (const name of element.getAttributeNames()) {
		if (name !== 'style' && !allowed?.has(name.toLowerCase())) {
			element.removeAttribute(name);
		}
	}
	if (element.tagName === 'A') {
		const declaredHref = element.getAttribute('href') ?? '';
		// An empty href resolves to this page, which would turn a named anchor into a link to the app.
		const href = declaredHref.trim() ? resolveAllowedUrl(declaredHref, LinkSchemes, baseUrl) : null;
		if (!href) {
			element.removeAttribute('href');
			element.removeAttribute('target');
			return true;
		}
		element.setAttribute('href', href);
		if (element.getAttribute('target') === '_blank') {
			element.setAttribute('rel', 'noopener noreferrer');
		}
	}
	if (element.tagName === 'IMG') {
		const declaredSrc = element.getAttribute('src') ?? '';
		const src = declaredSrc.trim() ? resolveAllowedUrl(declaredSrc, ImageSchemes, baseUrl) : null;
		if (!src) {
			return false;
		}
		element.setAttribute('src', src);
	}
	return true;
};

const isWordSpacerParagraph = (element: HTMLElement) => {
	if (element.tagName !== 'P' || !(element.getAttribute('class') || '').toLowerCase().includes('msonormal')) {
		return false;
	}
	return !element.querySelector('img,br,table') && element.textContent?.replace(/[\s ]/g, '') === '';
};

const replaceWithChildren = (element: HTMLElement) => {
	const parent = element.parentNode;
	if (!parent) {
		return;
	}
	while (element.firstChild) {
		parent.insertBefore(element.firstChild, element);
	}
	parent.removeChild(element);
};

/**
 * Depth-first over a snapshot of each node's children, so replacing or removing a child cannot make the
 * walk skip its siblings. Unwrapping waits until the walk ends: done inline it would hoist the children
 * past that snapshot, and they would reach the clipboard unsanitized.
 */
const sanitizeTree = (root: HTMLElement, doc: Document, options: ISanitizeCopiedHtmlOptions) => {
	const surfaceColor = toHexColor(options.surfaceColor);
	const surfaceBackground = toHexColor(options.surfaceBackgroundColor);
	const stack: { depth: number; node: Node }[] = [{ depth: 0, node: root }];
	const unwrapQueue: HTMLElement[] = [];
	let visited = 0;
	while (stack.length) {
		const { depth, node } = stack.pop();
		if (++visited > NodesMax || depth > DepthMax) {
			return false;
		}
		for (const child of Array.from(node.childNodes)) {
			if (child.nodeType === Node.COMMENT_NODE) {
				node.removeChild(child);
				continue;
			}
			if (child.nodeType !== Node.ELEMENT_NODE) {
				continue;
			}
			const element = child as HTMLElement;
			const tag = element.tagName;
			if (element.namespaceURI !== HtmlNamespaceUri || DiscardedTags.has(tag)) {
				node.removeChild(element);
				continue;
			}
			if (element.getAttribute('data-placeholder') === 'true') {
				const value = element.getAttribute('data-placeholder-value') ?? '';
				const symbol = options.placeholderSymbols?.[value] ?? `{{${element.textContent?.trim() ?? ''}}}`;
				node.replaceChild(doc.createTextNode(symbol), element);
				continue;
			}
			if (isWordSpacerParagraph(element)) {
				node.removeChild(element);
				continue;
			}
			const declaredWeight = rewriteStyle(element, surfaceColor, surfaceBackground);
			if (!rewriteAttributes(element, options.baseUrl)) {
				node.removeChild(element);
				continue;
			}
			// Google Docs wraps its fragment in `<b style="font-weight:normal">`; keeping the tag after
			// the style is dropped would bold the whole selection.
			const isFakeBold = (tag === 'B' || tag === 'STRONG') && !!declaredWeight && !BoldWeights.has(declaredWeight);
			stack.push({ depth: depth + 1, node: element });
			if (!AllowedTags.has(tag) || isFakeBold) {
				unwrapQueue.push(element);
			}
		}
	}
	for (const element of unwrapQueue) {
		replaceWithChildren(element);
	}
	return true;
};

const isBlockElement = (node: Node | null) =>
	node?.nodeType === Node.ELEMENT_NODE && BlockTags.has((node as HTMLElement).tagName);

/**
 * Collapses whitespace the way rendering does. Stored markup has a newline between every block, and the
 * text flavour would otherwise turn each one into a blank line.
 */
const collapseWhitespace = (root: HTMLElement, doc: Document) => {
	const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	const textNodes: Text[] = [];
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		textNodes.push(node as Text);
	}
	for (const node of textNodes) {
		const collapsed = (node.nodeValue ?? '').replace(WhitespaceRun, ' ');
		const parent = node.parentNode;
		const isBlockEdge = (parent === root || isBlockElement(parent)) && (!node.previousSibling || !node.nextSibling);
		const bordersBlock = isBlockEdge || isBlockElement(node.previousSibling) || isBlockElement(node.nextSibling);
		if (collapsed === ' ' && bordersBlock) {
			node.remove();
		} else {
			node.nodeValue = collapsed;
		}
	}
};

/** Returns null when the fragment is empty or past the bounds, meaning: leave the clipboard alone. */
export const sanitizeCopiedHtml = (
	html: string,
	options: ISanitizeCopiedHtmlOptions = {}
): ISanitizedClipboardContent | null => {
	if (!html || html.length > InputCharsMax) {
		return null;
	}
	const doc = new DOMParser().parseFromString(html, 'text/html');
	if (!doc?.body) {
		return null;
	}
	if (!sanitizeTree(doc.body, doc, options)) {
		return null;
	}
	collapseWhitespace(doc.body, doc);
	const sanitizedHtml = doc.body.innerHTML;
	// The text flavour is what "paste without formatting" and every plain-text target reads, so it has
	// to keep the line structure. `textContent` would run all 47 paragraphs together into one line.
	const text = convertHtmlStringValueToPlainTextLines(sanitizedHtml).replace(/^[ \t]+|[ \t]+$/gm, '');
	return { html: sanitizedHtml, text };
};

/** Inline ancestors carry formatting the selected text wears, so a selection inside a link stays a link. */
const InlineAncestorTags = new Set(['A', 'B', 'EM', 'I', 'S', 'SPAN', 'STRIKE', 'STRONG', 'SUB', 'SUP', 'U']);
/** Rows and sections are only valid inside their table, so wrapping continues up to it. */
const TablePartTags = new Set(['TBODY', 'TFOOT', 'THEAD', 'TR']);
/** A complete structure: wrap it and stop, since everything above it is layout. */
const StructureTags = new Set(['OL', 'TABLE', 'UL']);

/**
 * `cloneContents` drops the range's common ancestor, so a selection across list items loses its `<ul>`
 * and one inside a link loses the link. Re-wrap in those ancestors, stopping at the first text block so
 * a selection inside one paragraph still pastes inline.
 */
const wrapInSelectionAncestors = (range: Range, root: HTMLElement) => {
	const fragment: Node = range.cloneContents();
	const common = range.commonAncestorContainer;
	let ancestor = common.nodeType === Node.ELEMENT_NODE ? (common as HTMLElement) : common.parentElement;
	if (!ancestor || !root.contains(ancestor)) {
		return fragment;
	}
	let wrapped = fragment;
	for (let depth = 0; ancestor && ancestor !== root && depth < DepthMax; depth++) {
		const tag = ancestor.tagName;
		if (!InlineAncestorTags.has(tag) && !TablePartTags.has(tag) && !StructureTags.has(tag)) {
			break;
		}
		const wrapper = ancestor.cloneNode(false);
		wrapper.appendChild(wrapped);
		wrapped = wrapper;
		if (StructureTags.has(tag)) {
			break;
		}
		ancestor = ancestor.parentElement;
	}
	return wrapped;
};

/** Serializes the selection as stored markup, keeping the list, table and inline context it sits in. */
export const cloneSelectionHtml = (selection: Selection, root: HTMLElement) => {
	const container = document.createElement('div');
	for (let i = 0; i < selection.rangeCount; i++) {
		container.appendChild(wrapInSelectionAncestors(selection.getRangeAt(i), root));
	}
	return container.innerHTML;
};
